import { type FC, useCallback, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { Button } from '../../components/Button/Button'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { type PageMetadata } from '../../models/PageMetadata'
import { type InviteState } from '../../store/auth/auth'
import { BulkInvitationModal } from './BulkInvitationModal'
import { InviteUserModal } from './InviteUserModal'
import { resetPages } from './useTeamListUrlState'

export interface InviteUserActionsProps {
  usersPage: PageMetadata | undefined
}

export const InviteUserActions: FC<InviteUserActionsProps> = ({ usersPage }) => {
  const { activeTab = null } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { tracked } = useTracking()
  const { getValue, quotas } = useZocksQuota()
  const [showInviteUserModal, setShowInviteUserModal] = useState<boolean>(activeTab === 'invite')
  const [showBulkInvitationModal, setShowBulkInvitationModal] = useState<boolean>(activeTab === 'bulk-invite')

  const handleInviteUserOnClick = tracked(
    () => {
      setShowInviteUserModal(true)
      navigate({ pathname: '/account/manage-team/invite', search: searchParams.toString() }, { replace: true })
    },
    { eventName: TrackEvent.INVITE_USER_CLICKED },
  )

  const handleBulkInvitationOnClick = tracked(
    () => {
      setShowBulkInvitationModal(true)
      navigate({ pathname: '/account/manage-team/bulk-invite', search: searchParams.toString() }, { replace: true })
    },
    { eventName: TrackEvent.BULK_INVITE_USER_CLICKED },
  )

  const invitedState = useRef<InviteState | null>(null)
  const bulkInvitedState = useRef<InviteState | null>(null)

  const handleInvited = (state: InviteState) => {
    invitedState.current = state
  }

  const handleBulkInvited = (state: InviteState) => {
    bulkInvitedState.current = state
  }

  const handleInviteUserModalHide = () => {
    setShowInviteUserModal(false)
    const params = new URLSearchParams(searchParams)
    if (invitedState.current === 'requested') {
      params.set('state', 'requested')
      resetPages(params)
    }
    invitedState.current = null
    navigate({ pathname: '/account/manage-team/members', search: params.toString() }, { replace: true })
  }

  const handleBulkInvitationModalHide = () => {
    setShowBulkInvitationModal(false)
    if (bulkInvitedState.current) {
      const params = new URLSearchParams(searchParams)
      params.set('state', bulkInvitedState.current)
      resetPages(params)
      bulkInvitedState.current = null
      navigate({ pathname: '/account/manage-team/members', search: params.toString() }, { replace: true })
      return
    }
    navigate(`/account/manage-team/pending-invitations`, { replace: true })
  }

  const checkInvitationEnabled = useCallback(() => {
    if (usersPage != null && quotas != null) {
      const { value: maxSeats } = getValue(quotas.data, 'identity.maxSeats')
      return maxSeats > usersPage.totalElements || maxSeats > 1 || maxSeats === null
    }
    return false
  }, [usersPage, quotas, getValue])

  return (
    <>
      {showInviteUserModal ? <InviteUserModal onHide={handleInviteUserModalHide} onInvited={handleInvited} /> : null}
      {showBulkInvitationModal ? <BulkInvitationModal onHide={handleBulkInvitationModalHide} onInvited={handleBulkInvited} /> : null}
      <div className='d-flex gap-2'>
        {usersPage && quotas ? (
          <ScopedBlock
            scope={{
              scope: 'consoleuser:invite',
              superScope: ['consoleuser:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <ScopedBlock authActions={[checkInvitationEnabled]} missingScopeAction={MissingScopeAction.UPGRADE}>
              <Button variant='primary' onClick={handleInviteUserOnClick}>
                <i className='bi bi-plus' />
                Invite user
              </Button>
              <ScopedBlock
                missingScopeAction={MissingScopeAction.DISABLE}
                quota={{
                  quota: 'auth.bulkInvite.value',
                }}
              >
                <Button variant='primary' onClick={handleBulkInvitationOnClick}>
                  <i className='bi bi-plus' />
                  Bulk invitation
                </Button>
              </ScopedBlock>
            </ScopedBlock>
          </ScopedBlock>
        ) : null}
      </div>
    </>
  )
}
